'use client';

import { CopyIcon } from 'lucide-react';

import { useAdmin } from '@/app/providers';
import { Button } from '@/components/ui/button';
import { getTimeGroups } from '@/components/WorldClock';
import { getCompetitionName } from '@/utils';
import { FragmentOf, readFragment, type ResultOf } from '@/utils/graphql';
import { timezones } from '@/utils/world-clock-timezones';
import { matchdayHeaderTweetFragment } from './matchdayHeaderTweetFragment';

export function MatchdayHeaderTweet({
  matchday: _matchday,
}: {
  matchday: FragmentOf<typeof matchdayHeaderTweetFragment>;
}) {
  const isAdmin = useAdmin();

  if (isAdmin !== true) {
    return;
  }

  const matchday = readFragment(matchdayHeaderTweetFragment, _matchday);

  return (
    <Button
      variant="outline"
      size="icon"
      className="size-6 cursor-pointer"
      onClick={() => {
        const text = getTweetMessage(matchday);
        navigator.clipboard.writeText(text);
      }}
    >
      <CopyIcon className="size-3" />
    </Button>
  );
}

function getTweetMessage(
  matchday: ResultOf<typeof matchdayHeaderTweetFragment>,
) {
  const competitionName =
    matchday.season!.competition!.parent?.name ||
    matchday.season!.competition!.name;
  const editionName = getCompetitionName(matchday.season!.competition!);
  const seasonName = matchday.season!.name;
  const timeGroups = getTimeGroups(
    new Date(matchday.start_datetime!),
    timezones,
  )
    .map((timeGroup) => {
      const time = timeGroup.localeString.slice(12);
      const labelToSuffix: Record<string, string> = {
        'EE. UU. (Pacífico)': 'PT',
        'EE. UU. (Central)': 'CT',
        'EE. UU. (Este)': 'ET',
      };
      const flags = timeGroup.timezones
        .map(({ label, flag }) => flag + (labelToSuffix[label] ?? ''))
        .join('');
      return `${time} ${flags}`;
    })
    .join('\n');
  const hashtag = `#${(competitionName === 'FMS' ? editionName : competitionName).replaceAll(' ', '')}`;
  const countryFlag = matchday.country?.emoji || '';

  return `
${editionName} - ${seasonName}

Hoy se realiza la ${matchday.name}!

${timeGroups}

${hashtag} ${countryFlag}
  `.trim();
}
